import { useQuery } from '@tanstack/react-query';
import { dashboardService } from '../services/dashboard.service';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import {
  ChartBarIcon,
  UserGroupIcon,
  PaperAirplaneIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import type { DashboardData, CSATAlert } from '../types';

export default function Dashboard() {
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => dashboardService.getDashboard(),
  });

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            Failed to load dashboard
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { stats, csat_trend = [], top_clients = [], bottom_clients = [], alerts = [], recent_responses = [] } = data;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of your CSAT performance and recent activity.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Overall CSAT Score"
          value={`${(stats?.overall_csat ?? 0).toFixed(1)}%`}
          icon={ChartBarIcon}
          trend={stats?.csat_trend}
          trendLabel="vs last period"
          color="indigo"
        />
        <StatCard
          title="Total Clients"
          value={(stats?.total_clients ?? 0).toString()}
          icon={UserGroupIcon}
          color="blue"
        />
        <StatCard
          title="Response Rate"
          value={`${(stats?.response_rate ?? 0).toFixed(1)}%`}
          icon={ArrowTrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Pending Surveys"
          value={(stats?.pending_surveys ?? 0).toString()}
          icon={PaperAirplaneIcon}
          color="amber"
        />
      </div>

      {/* Charts and Alerts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* CSAT Trend Chart */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            CSAT Score Trend
          </h2>
          {csat_trend.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={csat_trend}
                  margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="csatGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => { try { return format(parseISO(value), 'MMM d'); } catch { return ''; } }}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    labelFormatter={(value) => { try { return format(parseISO(value as string), 'MMM d, yyyy'); } catch { return String(value); } }}
                    formatter={(value: any) => [`${Number(value || 0).toFixed(1)}%`, 'CSAT Score']}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fill="url(#csatGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-72 text-gray-400">
              <p>No trend data available yet.</p>
            </div>
          )}
        </div>

        {/* Alerts Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alerts</h2>
          {alerts.length > 0 ? (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {alerts.map((alert) => (
                <AlertItem key={alert.id} alert={alert} />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400">
              <div className="text-center">
                <CheckCircleIcon className="mx-auto h-10 w-10 text-green-400" />
                <p className="mt-2 text-sm">No alerts at this time.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Top/Bottom Clients + Recent Responses */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Top Performing Clients */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Top Performing Clients
          </h2>
          {top_clients.length > 0 ? (
            <div className="space-y-3">
              {top_clients.map((client: any) => (
                <ClientScoreRow key={client.id || client.client_id} client={client} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              No client data available.
            </p>
          )}
        </div>

        {/* Needs Attention */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Needs Attention
          </h2>
          {bottom_clients.length > 0 ? (
            <div className="space-y-3">
              {bottom_clients.map((client: any) => (
                <ClientScoreRow key={client.id || client.client_id} client={client} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              No client data available.
            </p>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Recent Responses
          </h2>
          {recent_responses.length > 0 ? (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {recent_responses.map((response: any, idx: number) => (
                <div
                  key={response.id || idx}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {response.first_name && response.last_name
                        ? `${response.first_name} ${response.last_name}`
                        : response.contact_name || 'Anonymous'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {response.client_name || ''}
                      {(response.completed_at || response.submitted_at)
                        ? ` · ${format(parseISO(response.completed_at || response.submitted_at), 'MMM d, yyyy')}`
                        : ''}
                    </p>
                  </div>
                  <ScoreBadge score={response.csat_score ?? 0} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              No recent responses.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Sub-components =====

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  trend?: number;
  trendLabel?: string;
  color: 'indigo' | 'blue' | 'green' | 'amber';
}

const colorMap = {
  indigo: {
    bg: 'bg-indigo-50',
    icon: 'text-indigo-600',
    ring: 'ring-indigo-100',
  },
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-600',
    ring: 'ring-blue-100',
  },
  green: {
    bg: 'bg-green-50',
    icon: 'text-green-600',
    ring: 'ring-green-100',
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-600',
    ring: 'ring-amber-100',
  },
};

function StatCard({ title, value, icon: Icon, trend, trendLabel, color }: StatCardProps) {
  const colors = colorMap[color];

  return (
    <div className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 rounded-lg ${colors.bg} p-3 ring-1 ${colors.ring}`}>
          <Icon className={`h-6 w-6 ${colors.icon}`} aria-hidden="true" />
        </div>
        <div className="ml-4 flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 truncate">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
      {trend !== undefined && (
        <div className="mt-3 flex items-center text-sm">
          {trend >= 0 ? (
            <ArrowUpIcon className="h-4 w-4 text-green-500 mr-1" />
          ) : (
            <ArrowDownIcon className="h-4 w-4 text-red-500 mr-1" />
          )}
          <span
            className={trend >= 0 ? 'text-green-600' : 'text-red-600'}
          >
            {Math.abs(trend).toFixed(1)}%
          </span>
          {trendLabel && (
            <span className="ml-1 text-gray-400">{trendLabel}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AlertItem({ alert }: { alert: CSATAlert }) {
  const alertStyles = {
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-500',
      text: 'text-red-800',
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'text-amber-500',
      text: 'text-amber-800',
    },
    improvement: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-500',
      text: 'text-green-800',
    },
  };

  const style = alertStyles[alert.type];

  return (
    <div className={`rounded-md p-3 ${style.bg} border ${style.border}`}>
      <div className="flex items-start">
        <ExclamationTriangleIcon
          className={`h-5 w-5 ${style.icon} flex-shrink-0 mt-0.5`}
          aria-hidden="true"
        />
        <div className="ml-2 min-w-0 flex-1">
          <p className={`text-sm font-medium ${style.text}`}>
            {alert.client_name}
            {alert.project_name && (
              <span className="font-normal"> — {alert.project_name}</span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-gray-600">{alert.message}</p>
        </div>
        <ScoreBadge score={alert.score} />
      </div>
    </div>
  );
}

function ClientScoreRow({
  client,
}: {
  client: any;
}) {
  const name = client.client_name ?? client.name ?? 'Unknown';
  const score = client.csat_score ?? client.score ?? 0;
  const id = client.client_id ?? client.id;
  return (
    <div key={id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">
          {name}
        </p>
        <p className="text-xs text-gray-500">
          {client.response_count ?? 0} responses
        </p>
      </div>
      <ScoreBadge score={score} />
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  let bgColor = 'bg-green-100 text-green-800';
  if (score < 60) {
    bgColor = 'bg-red-100 text-red-800';
  } else if (score < 80) {
    bgColor = 'bg-amber-100 text-amber-800';
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${bgColor}`}
    >
      {score}%
    </span>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div>
        <div className="h-8 w-40 bg-gray-200 rounded" />
        <div className="mt-2 h-4 w-72 bg-gray-200 rounded" />
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg bg-white shadow px-6 py-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-gray-200" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-7 w-16 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white rounded-lg shadow p-6">
          <div className="h-6 w-40 bg-gray-200 rounded mb-4" />
          <div className="h-72 bg-gray-100 rounded" />
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="h-6 w-24 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
